"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  RichText,
  useSitecore,
  type RichTextField,
} from "@sitecore-content-sdk/nextjs";
import styles from "./RichTextWithTable.module.scss";

interface RichTextWithTableProps {
  field?: RichTextField;
  className?: string;
  tag?: string;
}

const applyLabelsToAllCells = (
  headers: string[],
  contentRows: HTMLTableRowElement[],
): void => {
  for (const row of contentRows) {
    const cells = Array.from(row.querySelectorAll("td, th"));
    for (const [index, cell] of cells.entries()) {
      const htmlCell = cell as HTMLElement;
      if (headers[index] && !htmlCell.dataset.label) {
        htmlCell.dataset.label = headers[index];
      }
    }
  }
};

const RichTextWithTable = ({
  field,
  className = "",
  tag = "div",
  ...props
}: RichTextWithTableProps) => {
  const {
    page: { mode },
  } = useSitecore();
  const isPageEditing = mode?.isEditing;

  const contentRef = useRef<HTMLDivElement>(null);
  const hasTable = useMemo(
    () => !!field?.value?.includes("table"),
    [field?.value],
  );

  const applyTableFormatting = useCallback(() => {
    if (!contentRef.current || !hasTable) return;
    const tables = contentRef.current.querySelectorAll("table");

    for (const table of tables) {
      const thead = table.querySelector("thead");
      const tbody = table.querySelector("tbody");
      if (!tbody) continue;

      const rows = Array.from(tbody.querySelectorAll("tr"));
      if (rows.length === 0) continue;

      const theadRow = thead?.querySelector("tr");
      let headers: string[];
      let contentRows: HTMLTableRowElement[];

      if (theadRow) {
        headers = Array.from(theadRow.querySelectorAll("th")).map(
          (cell) => cell.textContent?.trim() || "",
        );
        contentRows = rows;
        theadRow.classList.add(styles.headerRow);
        table.classList.add(styles.hasHeaderRow);
      } else {
        rows[0].classList.add(styles.headerRow);
        table.classList.add(styles.hasNoHeaderRow);
        headers = Array.from(rows[0].querySelectorAll("td, th")).map(
          (cell) => cell.textContent?.trim() || "",
        );
        contentRows = rows.slice(1);
      }

      applyLabelsToAllCells(headers, contentRows);
    }
  }, [hasTable]);
  useEffect(() => {
    if (!contentRef.current) return;

    const observer = new MutationObserver(() => {
      applyTableFormatting();
    });

    observer.observe(contentRef.current, {
      childList: true,
      subtree: true,
    });

    applyTableFormatting();

    return () => observer.disconnect();
  }, [applyTableFormatting]);

  if (!hasTable) {
    return (
      <RichText
        field={field}
        tag={tag}
        className={`richtext-content ${className} ${
          isPageEditing
            ? `editMode ${styles.editMode} ${styles.richTextContent}`
            : `${styles.publishedView}`
        } w-full`}
        {...props}
      />
    );
  }

  if (!field?.value) return null;

  return (
    <div ref={contentRef}>
      <RichText
        field={field}
        className={`${styles.richTextContent} ${
          isPageEditing
            ? `editMode ${styles.editMode} w-full`
            : `${styles.publishedView}`
        } richtext-content ${className}`}
        tag={tag}
        {...props}
      />
    </div>
  );
};

export default RichTextWithTable;
export { RichTextWithTable };
